import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BusinessLayout from '../../components/layout/BusinessLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { 
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  PhoneIcon,
  CameraIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  DocumentArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import authService from '../../services/authService';
import { buildApiUrl } from '../../config/api';

interface RegisterDetail {
  id: string;
  date: string;
  status: string;
  notes: string;
  lastSaved?: string;
  session: {
    id: string;
    startTime: string;
    endTime: string;
    activity: {
      id: string;
      title: string;
      type: string;
      venue: {
        name: string;
        address: string;
      };
    };
  };
  attendance: Array<{
    id: string;
    present: boolean;
    checkInTime?: string;
    checkOutTime?: string;
    notes?: string;
    child: {
      id: string;
      firstName: string;
      lastName: string;
      yearGroup?: string;
      classGroup?: string;
    };
    booking: {
      parent: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
      };
    };
  }>;
}

const RegisterDetailPage: React.FC = () => {
  const { registerId } = useParams<{ registerId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [register, setRegister] = useState<RegisterDetail | null>(null);
  const [allRegisters, setAllRegisters] = useState<RegisterDetail[]>([]); // All registers for the activity
  const [currentRegisterIndex, setCurrentRegisterIndex] = useState(0); // Current date index for swipe
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string>('');
  const [medicalNotes, setMedicalNotes] = useState<{[key: string]: string}>({});
  const [uploadedImages, setUploadedImages] = useState<{[key: string]: string}>({});
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  useEffect(() => {
    if (registerId) {
      fetchRegisterDetail();
    }
  }, [registerId]);

  const fetchRegisterDetail = async () => {
    try {
      setLoading(true);
      const token = authService.getToken();
      if (!token) {
        toast.error('Please log in to view register');
        navigate('/login');
        return;
      }

      const response = await fetch(buildApiUrl(`/business/registers/${registerId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch register details');
      }

      const data = await response.json();
      if (data.success) {
        const registerData = data.data;
        setRegister(registerData);
        
        // If register has activity info, fetch all registers for that activity
        if (registerData.session?.activity?.id) {
          fetchAllRegistersForActivity(registerData.session.activity.id);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch register details');
      }
    } catch (error) {
      console.error('Error fetching register details:', error);
      toast.error('Failed to load register details');
      navigate('/business/registers');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRegistersForActivity = async (activityId: string) => {
    try {
      const token = authService.getToken();
      if (!token) return;

      const response = await fetch(buildApiUrl(`/registers/activity/${activityId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch all registers');
      }

      const data = await response.json();
      if (data.success) {
        // Transform and sort by date, include full attendance data
        const sortedRegisters = (data.data || [])
          .map((reg: any) => ({
            id: reg.id,
            date: reg.date || reg.session?.date,
            status: reg.status,
            notes: reg.notes,
            displayFields: reg.displayFields,
            session: reg.session,
            attendance: reg.attendance || []
          }))
          .sort((a: any, b: any) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return dateA - dateB; // Sort ascending (oldest first)
          });
        
        setAllRegisters(sortedRegisters);
        
        // Find current register index
        if (registerId) {
          const index = sortedRegisters.findIndex((r: any) => r.id === registerId);
          if (index >= 0) {
            setCurrentRegisterIndex(index);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching all registers:', error);
    }
  };

  // Format date as "Mon DD/MM"
  const formatDateShort = (dateString: string) => {
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = days[date.getDay()];
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${dayName} ${day}/${month}`;
  };

  // Swipe handlers
  const minSwipeDistance = 50;
  
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentRegisterIndex < allRegisters.length - 1) {
      // Swipe left - next date
      setCurrentRegisterIndex(currentRegisterIndex + 1);
      navigate(`/business/registers/${allRegisters[currentRegisterIndex + 1].id}`);
      fetchRegisterDetail();
    }
    
    if (isRightSwipe && currentRegisterIndex > 0) {
      // Swipe right - previous date
      setCurrentRegisterIndex(currentRegisterIndex - 1);
      navigate(`/business/registers/${allRegisters[currentRegisterIndex - 1].id}`);
      fetchRegisterDetail();
    }
  };
  
  const handlePreviousDate = () => {
    if (currentRegisterIndex > 0) {
      const newIndex = currentRegisterIndex - 1;
      setCurrentRegisterIndex(newIndex);
      navigate(`/business/registers/${allRegisters[newIndex].id}`);
      fetchRegisterDetail();
    }
  };
  
  const handleNextDate = () => {
    if (currentRegisterIndex < allRegisters.length - 1) {
      const newIndex = currentRegisterIndex + 1;
      setCurrentRegisterIndex(newIndex);
      navigate(`/business/registers/${allRegisters[newIndex].id}`);
      fetchRegisterDetail();
    }
  };

  const handleToggleAttendance = (attendanceId: string, present: boolean) => {
    setRegister(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        attendance: prev.attendance.map(att => 
          att.id === attendanceId ? { ...att, present } : att
        )
      };
    });
  };

  const handleSaveRegister = async () => {
    if (!register) return;
    
    try {
      setSaving(true);
      const token = authService.getToken();
      
      const response = await fetch(buildApiUrl(`/business/registers/${registerId}/save`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          attendance: register.attendance.map(att => ({
            id: att.id,
            present: att.present
          }))
        }),
      });

      if (response.ok) {
        const now = new Date();
        setLastSaved(`${Math.floor((Date.now() - now.getTime()) / 60000)} mins ago`);
        toast.success('Register saved successfully');
      } else {
        throw new Error('Failed to save register');
      }
    } catch (error) {
      console.error('Error saving register:', error);
      toast.error('Failed to save register');
    } finally {
      setSaving(false);
    }
  };

  const handleContactParent = (phone: string, parentName: string) => {
    // Open phone dialer or copy phone number
    if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
      window.location.href = `tel:${phone}`;
    } else {
      navigator.clipboard.writeText(phone);
      toast.success(`Phone number copied: ${phone}`);
    }
  };

  const handleMedicalNoteChange = (attendanceId: string, note: string) => {
    setMedicalNotes(prev => ({
      ...prev,
      [attendanceId]: note
    }));
  };

  const handleImageUpload = (attendanceId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Convert to base64 for preview (in real app, you'd upload to server)
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setUploadedImages(prev => ({
          ...prev,
          [attendanceId]: result
        }));
        toast.success('Image uploaded successfully');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExportRegister = async (format: 'csv' | 'excel' | 'pdf') => {
    if (!register || !register.session?.activity?.id) return;
    
    try {
      const token = authService.getToken();
      const activityId = register.session.activity.id;

      const response = await fetch(
        buildApiUrl(`/registers/export-course/${activityId}?format=${format}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export register');
      }

      // Get filename from response or create one
      const contentDisposition = response.headers.get('content-disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `register_${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xls' : 'csv'}`;

      // Get blob and create download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Register exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      console.error('Error exporting register:', error);
      toast.error('Failed to export register');
    }
  };

  const removeImage = (attendanceId: string) => {
    setUploadedImages(prev => {
      const newImages = { ...prev };
      delete newImages[attendanceId];
      return newImages;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'in-progress':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <BusinessLayout user={user}>
        <div className="bg-white min-h-screen">
          <div className="animate-pulse">
            <div className="h-16 bg-gray-200"></div>
            <div className="p-4">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
            <div className="p-4 space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </BusinessLayout>
    );
  }

  if (!register) {
    return (
      <BusinessLayout user={user}>
        <div className="bg-white min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Register not found</h3>
            <p className="text-gray-600 mb-4">The register you're looking for doesn't exist or you don't have access to it.</p>
            <button
              onClick={() => navigate('/business/registers')}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              Back to Registers
            </button>
          </div>
        </div>
      </BusinessLayout>
    );
  }

  return (
    <BusinessLayout user={user}>
      {/* Mobile Layout */}
      <div className="block md:hidden">
        <div className="bg-white min-h-screen">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <button
              onClick={() => navigate('/business/registers')}
              className="p-2"
            >
              <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
            </button>
            <h1 className="text-lg font-bold text-gray-900">Register</h1>
            <div className="flex gap-1">
              <button
                onClick={() => handleExportRegister('csv')}
                className="p-2 text-gray-600 hover:text-gray-900"
                title="Export CSV"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleExportRegister('pdf')}
                className="p-2 text-gray-600 hover:text-gray-900"
                title="Export PDF"
              >
                <DocumentArrowDownIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Event Details */}
          <div className="px-4 py-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {register.session.activity.title}
            </h2>
            <p className="text-sm text-gray-600">
              {new Date(register.date).toLocaleDateString('en-GB', {
                month: 'long',
                day: 'numeric'
              })}, {register.session.startTime} · {register.session.activity.venue.name}
            </p>
          </div>

          {/* Date Navigation */}
          {allRegisters.length > 1 && (
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <button
                onClick={handlePreviousDate}
                disabled={currentRegisterIndex === 0}
                className="p-2 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              
              <div className="flex items-center gap-2 flex-1 justify-center">
                <CalendarDaysIcon className="h-5 w-5 text-[#00806a]" />
                <span className="font-medium text-gray-900">
                  {new Date(register.date).toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
                <span className="text-sm text-gray-500">
                  ({currentRegisterIndex + 1} of {allRegisters.length})
                </span>
              </div>
              
              <button
                onClick={handleNextDate}
                disabled={currentRegisterIndex === allRegisters.length - 1}
                className="p-2 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          )}

          {/* Participants List */}
          <div 
            className="px-4"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {register.attendance.map((attendance, index) => {
              // Get displayFields from current register
              const displayFields = (register as any).displayFields as string[] | null || ['firstName'];
              
              // Helper to check if field should be displayed
              const shouldShowField = (field: string) => displayFields.includes(field);
              
              // Get child data based on displayFields
              const getChildField = (field: string) => {
                switch (field) {
                  case 'firstName': return attendance.child.firstName;
                  case 'lastName': return attendance.child.lastName;
                  case 'school': return (attendance.child as any).school;
                  case 'class': return (attendance.child as any).class || (attendance.child as any).classGroup;
                  case 'yearGroup': return attendance.child.yearGroup;
                  case 'dateOfBirth': return (attendance.child as any).dateOfBirth;
                  case 'allergies': return (attendance.child as any).allergies;
                  case 'medicalInfo': return (attendance.child as any).medicalInfo;
                  case 'consentToWalkHome': return (attendance.child as any).permissions?.consentToWalkHome;
                  case 'consentToPhotography': return (attendance.child as any).permissions?.consentToPhotography;
                  case 'consentToFirstAid': return (attendance.child as any).permissions?.consentToFirstAid;
                  case 'consentToEmergencyContact': return (attendance.child as any).permissions?.consentToEmergencyContact;
                  default: return null;
                }
              };
              
              return (
                <div key={attendance.id} className={`py-4 ${index !== register.attendance.length - 1 ? 'border-b border-gray-200' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {shouldShowField('firstName') && (
                          <h3 className="font-medium text-gray-900">
                            {getChildField('firstName')}
                            {shouldShowField('lastName') && ` ${getChildField('lastName') || ''}`}
                          </h3>
                        )}
                        {shouldShowField('yearGroup') && getChildField('yearGroup') && (
                          <span className="text-xs text-gray-500">
                            {getChildField('yearGroup')}
                          </span>
                        )}
                        {shouldShowField('class') && getChildField('class') && (
                          <span className="text-xs text-gray-500">
                            Class: {getChildField('class')}
                          </span>
                        )}
                        {shouldShowField('school') && getChildField('school') && (
                          <span className="text-xs text-gray-500">
                            {getChildField('school')}
                          </span>
                        )}
                      </div>
                      
                      {/* Permissions */}
                      <div className="flex flex-wrap gap-2 mt-1">
                        {shouldShowField('consentToWalkHome') && getChildField('consentToWalkHome') !== undefined && (
                          <span className={`text-xs px-2 py-0.5 rounded ${getChildField('consentToWalkHome') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            Walk Home: {getChildField('consentToWalkHome') ? 'Yes' : 'No'}
                          </span>
                        )}
                        {shouldShowField('consentToPhotography') && getChildField('consentToPhotography') !== undefined && (
                          <span className={`text-xs px-2 py-0.5 rounded ${getChildField('consentToPhotography') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            Photo: {getChildField('consentToPhotography') ? 'Yes' : 'No'}
                          </span>
                        )}
                      </div>
                      
                      {/* Medical Info */}
                      {shouldShowField('allergies') && getChildField('allergies') && (
                        <p className="text-xs text-orange-600 mt-1">
                          Allergies: {getChildField('allergies')}
                        </p>
                      )}
                      {shouldShowField('medicalInfo') && getChildField('medicalInfo') && (
                        <p className="text-xs text-orange-600 mt-1">
                          Medical: {getChildField('medicalInfo')}
                        </p>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                    {/* Present Button */}
                    <button
                      onClick={() => handleToggleAttendance(attendance.id, true)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        attendance.present 
                          ? 'bg-green-600 text-white shadow-md' 
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      }`}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    
                    {/* Absent Button */}
                    <button
                      onClick={() => handleToggleAttendance(attendance.id, false)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        !attendance.present 
                          ? 'bg-red-600 text-white shadow-md' 
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      }`}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                    
                    {/* Medical Button */}
                    <button
                      onClick={() => {
                        const note = prompt('Medical notes:', medicalNotes[attendance.id] || '');
                        if (note !== null) {
                          handleMedicalNoteChange(attendance.id, note);
                        }
                      }}
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        medicalNotes[attendance.id] 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                    
                    {/* Photo Upload Button */}
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(attendance.id, e)}
                        className="absolute inset-0 w-8 h-8 opacity-0 cursor-pointer"
                      />
                      <button
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          uploadedImages[attendance.id] 
                            ? 'bg-teal-500 text-white' 
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        <CameraIcon className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {/* Contact Button */}
                    <button
                      onClick={() => handleContactParent(attendance.booking.parent.phone, attendance.booking.parent.firstName)}
                      className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center"
                    >
                      <PhoneIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Save Button */}
          <div className="px-4 py-6">
            <button
              onClick={handleSaveRegister}
              disabled={saving}
              className="w-full bg-teal-700 hover:bg-teal-800 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              {saving ? 'Saving...' : 'Save Register'}
            </button>
            {lastSaved && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Last saved {lastSaved}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Layout - Table View with Dates as Columns */}
      <div className="hidden md:block">
        <div className="max-w-full mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/business/registers')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{register.session.activity.title}</h1>
                <p className="text-gray-600">{register.session.activity.venue.name}</p>
              </div>
            </div>
            
            {/* Export Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleExportRegister('csv')}
                className="flex items-center gap-2"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExportRegister('excel')}
                className="flex items-center gap-2"
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                Excel
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExportRegister('pdf')}
                className="flex items-center gap-2"
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                PDF
              </Button>
            </div>
          </div>

          {/* Table View - Dates as Columns */}
          {allRegisters.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 border-r border-gray-200">
                      <input type="checkbox" className="rounded" />
                      <div className="mt-2">Child</div>
                      <div className="text-xs font-normal text-gray-400 mt-1">Owe</div>
                    </th>
                    {allRegisters.map((reg) => (
                      <th key={reg.id} className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                        <div>{formatDateShort(reg.date)}</div>
                        <div className="mt-1">
                          <CalendarDaysIcon className="h-4 w-4 text-gray-400 mx-auto" />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Get unique children across all registers */}
                  {(() => {
                    // Collect all unique children from all registers
                    const childMap = new Map<string, {
                      child: any;
                      parent: any;
                      attendances: Map<string, any>; // registerId -> attendance
                    }>();

                    allRegisters.forEach((reg) => {
                      (reg.attendance || []).forEach((att: any) => {
                        const childId = att.child.id;
                        if (!childMap.has(childId)) {
                          childMap.set(childId, {
                            child: att.child,
                            parent: att.booking.parent,
                            attendances: new Map()
                          });
                        }
                        childMap.get(childId)!.attendances.set(reg.id, att);
                      });
                    });

                    // Get displayFields from first register
                    const displayFields = (allRegisters[0] as any)?.displayFields || ['firstName'];
                    
                    return Array.from(childMap.values()).map(({ child, parent, attendances }) => {
                      const shouldShowField = (field: string) => displayFields.includes(field);
                      const getChildField = (field: string) => {
                        switch (field) {
                          case 'firstName': return child.firstName;
                          case 'lastName': return child.lastName;
                          case 'school': return (child as any).school;
                          case 'class': return (child as any).class || (child as any).classGroup;
                          case 'yearGroup': return child.yearGroup;
                          default: return null;
                        }
                      };

                      return (
                        <tr key={child.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 sticky left-0 bg-white z-10 border-r border-gray-200">
                            <div className="flex items-center">
                              <input type="checkbox" className="rounded mr-3" />
                              <div>
                                <div className="font-medium text-gray-900">
                                  {shouldShowField('firstName') && getChildField('firstName')}
                                  {shouldShowField('lastName') && ` ${getChildField('lastName') || ''}`}
                                </div>
                                <div className="text-xs text-gray-500">
                                  ({parent.firstName} {parent.lastName})
                                </div>
                                {shouldShowField('yearGroup') && getChildField('yearGroup') && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {getChildField('yearGroup')}
                                  </div>
                                )}
                                {shouldShowField('class') && getChildField('class') && (
                                  <div className="text-xs text-gray-500">
                                    Class: {getChildField('class')}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="mt-2">
                              <button className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded">
                                00:00
                              </button>
                            </div>
                          </td>
                          {allRegisters.map((reg) => {
                            const attendance = attendances.get(reg.id);
                            return (
                              <td key={reg.id} className="px-3 py-3 text-center">
                                <div className="flex flex-col items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={attendance?.present || false}
                                    onChange={(e) => {
                                      if (attendance) {
                                        handleToggleAttendance(attendance.id, e.target.checked);
                                      }
                                    }}
                                    className="rounded"
                                  />
                                  <div className="text-xs text-gray-400">
                                    <CalendarDaysIcon className="h-3 w-3 mx-auto" />
                                  </div>
                                  <button className="text-xs text-gray-400 hover:text-red-600">
                                    <TrashIcon className="h-3 w-3" />
                                  </button>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <p className="text-gray-500">No registers available for this activity</p>
            </div>
          )}

          {/* Save Button */}
          <div className="mt-6">
            <button
              onClick={handleSaveRegister}
              disabled={saving}
              className="w-full bg-teal-700 hover:bg-teal-800 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-lg transition-colors"
            >
              {saving ? 'Saving...' : 'Save Register'}
            </button>
            {lastSaved && (
              <p className="text-sm text-gray-500 text-center mt-2">
                Last saved {lastSaved}
              </p>
            )}
          </div>
        </div>
      </div>
    </BusinessLayout>
  );
};

export default RegisterDetailPage;
